import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Caixa global de entrada numérica do JEMS3. Mesma geometria/visual da
 * caixa filter-styled do datepicker (36px altura, radius/border/bg dos
 * tokens chrome). Conteúdo: <input type="number"> + 2 setas (↑/↓) à
 * direita pra incrementar/decrementar por `step`.
 *
 * Suporta `[(ngModel)]` via ControlValueAccessor.
 *
 * Uso:
 *   <app-number-input [(ngModel)]="qty" [min]="0" [max]="100" [step]="1"></app-number-input>
 */
@Component({
  selector: 'app-number-input',
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => NumberInputComponent),
    multi: true,
  }],
})
export class NumberInputComponent implements ControlValueAccessor, OnDestroy {
  @Input() min?: number;
  @Input() max?: number;
  @Input() step = 1;
  @Input() disabled = false;
  @Input() placeholder = '';
  /**
   * Texto descritivo do campo. Quando setado, renderiza rótulo acima da
   * caixa (mesma API/visual de `app-datetimepicker-input.description`).
   * Aceita chave i18n.
   */
  @Input() description?: string;
  /** Passos por PageUp/PageDown e wheel+Shift — multiplicador do `step`. */
  @Input() pageStep = 10;
  /** Casas decimais fixas exibidas (ex.: 2 → "0.50"). Sem isso (default),
   *  o valor aparece com a precisão nativa do número (comportamento atual,
   *  inalterado). Também usado para arredondar o valor committed, evitando
   *  artefatos de ponto flutuante em steps fracionários (0.1 + 0.2 etc). */
  @Input() decimals?: number;
  /** Esconde as setas +/- internas — usado quando o consumidor prefere
   *  renderizar seus próprios controles fora da caixa (ver `.hide-steppers`
   *  no SCSS, que devolve o padding que as setas ocupavam). */
  @Input() hideSteppers = false;
  @Output() valueChange = new EventEmitter<number | null>();
  @HostBinding('style.width') @Input() width?: string;

  value: number | null = null;
  /** Foco atual no input — gate p/ wheel (evita bump acidental ao rolar a página). */
  public focused = false;
  private onChange: (v: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** Timers do hold-to-repeat: `holdDelay` = atraso até começar a repetir;
   *  `holdInterval` = intervalo entre repetições depois disso. */
  private holdTimeout?: ReturnType<typeof setTimeout>;
  private holdInterval?: ReturnType<typeof setInterval>;
  private readonly HOLD_DELAY_MS = 400;
  private readonly HOLD_INTERVAL_MS = 60;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnDestroy(): void { this.stopHold(); }

  writeValue(v: number | null): void {
    this.value = v ?? null;
    // OnPush não detecta a mutação de `this.value` por si só; `markForCheck`
    // força a re-renderização da view quando o FormControl é atualizado
    // programaticamente (ex.: threshold-band fazendo `setValue`).
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: number | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; this.cdr.markForCheck(); }

  onInput(ev: Event): void {
    const raw = (ev.target as HTMLInputElement).value;
    const n = raw === '' ? null : Number(raw);
    this.commit(Number.isNaN(n as number) ? null : n);
  }

  /** Wheel sobre o input: incrementa/decrementa por `step` (Shift = pageStep×).
   *  Só ativa quando o input tem foco — evita bump acidental durante scroll da página. */
  onWheel(ev: WheelEvent): void {
    if (!this.focused || this.disabled) return;
    ev.preventDefault();
    const factor = ev.shiftKey ? this.pageStep : 1;
    this.bumpBy((ev.deltaY < 0 ? this.step : -this.step) * factor);
  }

  /** Teclado: PageUp/PageDown por `pageStep × step`. (Setas ↑↓ já funcionam
   *  nativamente via `<input type="number">`.) */
  onKeyDown(ev: KeyboardEvent): void {
    if (this.disabled) return;
    if (ev.key === 'PageUp')       { ev.preventDefault(); this.bumpBy(this.step * this.pageStep); }
    else if (ev.key === 'PageDown') { ev.preventDefault(); this.bumpBy(-this.step * this.pageStep); }
  }

  /** Início do hold-to-repeat: bump imediato + após `HOLD_DELAY_MS`, repete
   *  a cada `HOLD_INTERVAL_MS`. Chamado no `pointerdown` das setas. */
  startHold(delta: number): void {
    if (this.disabled) return;
    this.stopHold();
    this.bumpBy(delta);
    this.holdTimeout = setTimeout(() => {
      this.holdInterval = setInterval(() => this.bumpBy(delta), this.HOLD_INTERVAL_MS);
    }, this.HOLD_DELAY_MS);
  }
  stopHold(): void {
    if (this.holdTimeout)  { clearTimeout(this.holdTimeout);  this.holdTimeout  = undefined; }
    if (this.holdInterval) { clearInterval(this.holdInterval); this.holdInterval = undefined; }
  }

  private bumpBy(delta: number): void {
    if (this.disabled) return;
    const base = (this.value ?? 0) + delta;
    this.commit(this.clamp(base));
  }
  private clamp(n: number): number {
    if (this.max != null && n > this.max) return this.max;
    if (this.min != null && n < this.min) return this.min;
    return n;
  }
  get displayValue(): string | number {
    if (this.value === null || this.value === undefined) { return ''; }
    return this.decimals != null ? this.value.toFixed(this.decimals) : this.value;
  }

  private commit(v: number | null): void {
    if (v !== null && this.decimals != null) {
      v = Number(v.toFixed(this.decimals));
    }
    this.value = v;
    this.onChange(v);
    this.valueChange.emit(v);
    this.onTouched();
    // OnPush: força re-render p/ atualizar `[disabled]` das setas quando a
    // repetição atinge min/max (o hold pára automaticamente porque `<button
    // disabled>` bloqueia pointerdown/pointerup, mas o intervalo em andamento
    // continuaria clampando à toa até o pointerup).
    this.cdr.markForCheck();
  }

  onFocusInput(): void { this.focused = true; }
  onBlurInput(): void {
    this.focused = false;
    // Clampa o valor DIGITADO só ao sair do campo — clampar a cada tecla
    // brigava com o cursor (ex.: digitar "99999" virava "999997000", o
    // valor clampado sendo reinserido no meio da digitação). No blur não
    // há mais digitação em andamento, então corrigir é seguro.
    if (this.value !== null && this.value !== undefined) {
      const clamped = this.clamp(this.value);
      if (clamped !== this.value) {
        this.commit(clamped);
      }
    }
    this.onTouched();
  }
}
